import { loadStripe, type Stripe } from "@stripe/stripe-js";

export type StripeAccountTokenPerson = {
    firstname?: unknown;
    first_name?: unknown;
    lastname?: unknown;
    last_name?: unknown;
    birthday?: unknown;
    phone?: unknown;
    house_number?: unknown;
    street?: unknown;
    address?: unknown;
    city?: unknown;
    postcode?: unknown;
};

type StripeWithAccountToken = Stripe & {
    createToken(
        type: "account",
        data: { account: Record<string, unknown> }
    ): Promise<{ token?: { id: string } | null; error?: { message?: string } }>;
};

type StripeDob = {
    day: number;
    month: number;
    year: number;
};

function parseBirthday(value: unknown): StripeDob | null {
    const raw = String(value ?? "").trim();
    if (!raw) {
        return null;
    }

    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
        return {
            year: Number(isoMatch[1]),
            month: Number(isoMatch[2]),
            day: Number(isoMatch[3]),
        };
    }

    const isoDateTimeMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})T/);
    if (isoDateTimeMatch) {
        return {
            year: Number(isoDateTimeMatch[1]),
            month: Number(isoDateTimeMatch[2]),
            day: Number(isoDateTimeMatch[3]),
        };
    }

    const frMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (frMatch) {
        return {
            day: Number(frMatch[1]),
            month: Number(frMatch[2]),
            year: Number(frMatch[3]),
        };
    }

    return null;
}

function buildAddressLine(person: StripeAccountTokenPerson): string {
    const houseNumber = String(person.house_number ?? "").trim();
    const street = String(person.street ?? "").trim();
    const address = String(person.address ?? "").trim();

    if (houseNumber || street) {
        return [houseNumber, street].filter(Boolean).join(" ");
    }

    return address;
}

/**
 * Creates a Stripe Connect account token client-side (required in live mode for FR platforms).
 */
export async function createStripeAccountToken(
    publishableKey: string,
    person: StripeAccountTokenPerson
): Promise<string> {
    const stripe: Stripe | null = await loadStripe(publishableKey);
    if (!stripe) {
        throw new Error("Stripe.js failed to load.");
    }

    const dob = parseBirthday(person.birthday);
    if (!dob) {
        throw new Error("Invalid or missing birth date for Stripe account token.");
    }

    const firstName = String(person.firstname ?? person.first_name ?? "").trim();
    const lastName = String(person.lastname ?? person.last_name ?? "").trim();
    if (!firstName || !lastName) {
        throw new Error("Missing name for Stripe account token.");
    }

    const { token, error } = await (stripe as StripeWithAccountToken).createToken("account", {
        account: {
            business_type: "individual",
            individual: {
                first_name: firstName,
                last_name: lastName,
                address: {
                    line1: buildAddressLine(person),
                    city: String(person.city ?? "").trim(),
                    postal_code: String(person.postcode ?? "").trim(),
                    country: "FR",
                },
                dob,
                phone: String(person.phone ?? "").trim() || undefined,
            },
            tos_shown_and_accepted: true,
        },
    });

    if (error || !token?.id) {
        throw new Error(error?.message ?? "Stripe account token creation failed.");
    }

    return token.id;
}
